import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/ui/tabs";
import { Download, RefreshCw, Search, LogIn, History, Users as UsersIcon, X, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

/**
 * LoginReportDialog — Admin-only view of platform sign-in activity.
 *
 * Two tabs:
 * 1. Summary  → one row per user with their most-recent login (backed by
 *    `GET /api/admin/login-report`, sourced from `users.last_login`).
 * 2. Audit trail → full per-login history (backed by
 *    `GET /api/admin/login-events`, sourced from the `login_events`
 *    collection populated on every successful `/auth/login`).
 *
 * Both tabs support an Excel export of the same filtered dataset.
 */

const WINDOWS = [
  { value: "all", label: "All users" },
  { value: "7", label: "Logged in – last 7 days" },
  { value: "30", label: "Logged in – last 30 days" },
  { value: "90", label: "Logged in – last 90 days" },
  { value: "180", label: "Logged in – last 6 months" },
  { value: "365", label: "Logged in – last year" },
  { value: "never", label: "Never logged in" },
];

const EVENT_WINDOWS = [
  { value: "all", label: "All time" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "180", label: "Last 6 months" },
  { value: "365", label: "Last year" },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const activityBadge = (days) => {
  if (days == null) return { label: "Never", cls: "bg-slate-100 text-slate-600 border-slate-200" };
  if (days === 0) return { label: "Today", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" };
  if (days <= 7) return { label: `${days}d ago`, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" };
  if (days <= 30) return { label: `${days}d ago`, cls: "bg-lime-50 text-lime-800 border-lime-200" };
  if (days <= 90) return { label: `${days}d ago`, cls: "bg-amber-50 text-amber-800 border-amber-200" };
  return { label: `${days}d ago`, cls: "bg-rose-50 text-rose-800 border-rose-200" };
};

/** Shrink a raw User-Agent string down to something you can read in a table cell. */
const shortUA = (ua) => {
  if (!ua) return "—";
  const s = String(ua);
  const m = s.match(/(Chrome|Firefox|Safari|Edge|Opera|curl|Postman)[\/ ]([\d.]+)/i);
  if (m) return `${m[1]} ${m[2].split(".")[0]}`;
  return s.length > 40 ? s.slice(0, 37) + "…" : s;
};

// ── Summary tab ─────────────────────────────────────────────────────────
const SummaryTab = ({ onOpenHistoryForUser }) => {
  const [window, setWindow] = useState("all");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, logged_at_least_once: 0, never_logged_in: 0 });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [search, setSearch] = useState("");

  const load = async (w) => {
    setLoading(true);
    try {
      const res = await axios.get(`/admin/login-report`, { params: { window: w } });
      setRows(res.data?.rows || []);
      setStats({
        total: res.data?.total || 0,
        logged_at_least_once: res.data?.logged_at_least_once || 0,
        never_logged_in: res.data?.never_logged_in || 0,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load login report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(window); /* eslint-disable-line */ }, [window]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) =>
      [r.email, r.first_name, r.last_name, r.member_number, r.role]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`/admin/login-report/export`, { params: { window }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `login_report_${window}_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Login report downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-wrap items-center gap-3 pt-1 pb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-600">Window</span>
          <Select value={window} onValueChange={setWindow}>
            <SelectTrigger className="h-8 w-56" data-testid="login-report-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map((w) => (<SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, member #…"
            className="h-8 pl-7 text-sm"
            data-testid="login-report-search"
          />
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-800 text-xs" data-testid="login-report-stat-total">Total: {stats.total}</Badge>
          <Badge variant="outline" className="bg-lime-50 border-lime-200 text-lime-800 text-xs" data-testid="login-report-stat-active">Logged in ≥ once: {stats.logged_at_least_once}</Badge>
          <Badge variant="outline" className="bg-slate-100 border-slate-300 text-slate-700 text-xs" data-testid="login-report-stat-never">Never: {stats.never_logged_in}</Badge>
        </div>
        <Button size="sm" variant="outline" onClick={() => load(window)} disabled={loading} data-testid="login-report-refresh">
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={download} disabled={downloading || rows.length === 0} data-testid="login-report-download">
          <Download className="h-3 w-3 mr-1" />{downloading ? "Preparing…" : "Download Excel"}
        </Button>
      </div>

      <div className="flex-1 overflow-auto border rounded-md">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 sticky top-0">
              <TableHead>Member #</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Last login</TableHead>
              <TableHead>Activity</TableHead>
              <TableHead>Verified</TableHead>
              <TableHead className="text-right">History</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="py-8 text-center text-slate-500">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="py-8 text-center text-slate-500">No users match this filter.</TableCell></TableRow>
            ) : (
              filtered.map((r) => {
                const badge = activityBadge(r.days_since_login);
                return (
                  <TableRow key={r.id} data-testid={`login-row-${r.id}`}>
                    <TableCell className="text-xs">{r.member_number || "—"}</TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium">{r.last_name} {r.first_name}</div>
                      {r.middle_name && <div className="text-xs text-slate-500">{r.middle_name}</div>}
                    </TableCell>
                    <TableCell className="text-xs">{r.email}</TableCell>
                    <TableCell className="text-xs">
                      <Badge variant="outline" className={`text-[10px] ${r.role === "Administrator" ? "bg-purple-50 text-purple-800 border-purple-200" : "bg-slate-50 text-slate-700 border-slate-200"}`}>{r.role || "—"}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">{r.member_category || "—"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {r.last_login ? formatDate(r.last_login) : <span className="italic text-slate-400">never</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${badge.cls}`} data-testid={`login-activity-${r.id}`}>{badge.label}</Badge>
                    </TableCell>
                    <TableCell>
                      {r.is_verified ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200">Yes</Badge> : <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">No</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        onClick={() => onOpenHistoryForUser(r)}
                        disabled={!r.last_login}
                        data-testid={`open-history-${r.id}`}
                      >
                        <History className="h-3 w-3 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ── Audit-trail tab ─────────────────────────────────────────────────────
const AuditTab = ({ filterUser, onClearFilter }) => {
  const [window, setWindow] = useState("30");
  const [outcome, setOutcome] = useState("all");
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [returned, setReturned] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [search, setSearch] = useState("");
  const [suspicious, setSuspicious] = useState(null);
  const [showSuspicious, setShowSuspicious] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { window, outcome, limit: 500 };
      if (filterUser?.id) params.user_id = filterUser.id;
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(`/admin/login-events`, { params });
      setEvents(res.data?.events || []);
      setTotal(res.data?.total || 0);
      setReturned(res.data?.returned || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load audit trail");
    } finally {
      setLoading(false);
    }
  };

  // Suspicious-clusters call is independent of the outcome/user filter — we
  // always want the "last 24h across the whole platform" view so admins can
  // spot brute-force patterns even while browsing a single user.
  const loadSuspicious = async () => {
    try {
      const res = await axios.get(`/admin/login-events/suspicious`, {
        params: { window: 24, min_failures: 5 },
      });
      setSuspicious(res.data || null);
    } catch {
      setSuspicious(null);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [window, outcome, filterUser?.id]);
  useEffect(() => { loadSuspicious(); /* eslint-disable-line */ }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const params = { window, outcome, limit: 20000 };
      if (filterUser?.id) params.user_id = filterUser.id;
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(`/admin/login-events/export`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `login_events_${window}_${outcome}_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Audit trail downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Suspicious activity banner — shown when at least one IP has failed
          5+ times in the past 24h. Non-blocking; admins can dismiss it. */}
      {showSuspicious && suspicious && (suspicious.ip_clusters?.length > 0 || suspicious.email_clusters?.length > 0) && (
        <div className="mb-2 rounded-md border-2 border-rose-300 bg-rose-50 p-3" data-testid="suspicious-activity-banner">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0">
              <AlertTriangle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-bold text-rose-800">
                  Suspicious activity — {suspicious.total_failures} failed login{suspicious.total_failures === 1 ? "" : "s"} in the last {suspicious.window_hours}h
                </p>
                <div className="mt-1 space-y-0.5">
                  {(suspicious.ip_clusters || []).slice(0, 3).map((c) => (
                    <p key={c.ip} className="text-xs text-rose-700" data-testid={`suspicious-ip-${c.ip}`}>
                      <span className="font-mono font-semibold">{c.ip}</span> — {c.failures} failures targeting {c.distinct_emails.length} account{c.distinct_emails.length === 1 ? "" : "s"}
                      {c.distinct_emails.length > 0 && <span className="text-rose-600"> ({c.distinct_emails.slice(0, 3).join(", ")}{c.distinct_emails.length > 3 ? "…" : ""})</span>}
                    </p>
                  ))}
                  {(suspicious.email_clusters || []).slice(0, 2).map((c) => (
                    <p key={c.email} className="text-xs text-rose-700" data-testid={`suspicious-email-${c.email}`}>
                      Account <span className="font-semibold">{c.email}</span> — {c.failures} failed attempts from {c.distinct_ips.length} IP{c.distinct_ips.length === 1 ? "" : "s"}
                    </p>
                  ))}
                </div>
              </div>
            </div>
            <button
              className="text-rose-500 hover:text-rose-700"
              onClick={() => setShowSuspicious(false)}
              aria-label="Dismiss"
              data-testid="dismiss-suspicious"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 pt-1 pb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-600">Window</span>
          <Select value={window} onValueChange={setWindow}>
            <SelectTrigger className="h-8 w-44" data-testid="login-events-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EVENT_WINDOWS.map((w) => (<SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-600">Outcome</span>
          <Select value={outcome} onValueChange={setOutcome}>
            <SelectTrigger className="h-8 w-36" data-testid="login-events-outcome">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="success">Successful only</SelectItem>
              <SelectItem value="failure">Failed only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {filterUser ? (
          <Badge variant="outline" className="bg-purple-50 border-purple-200 text-purple-800 text-xs gap-1" data-testid="login-events-user-filter">
            Filter: {filterUser.last_name} {filterUser.first_name}
            <button onClick={onClearFilter} className="ml-1 hover:text-purple-900" aria-label="Clear filter">
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ) : (
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search by email…"
              className="h-8 pl-7 text-sm"
              data-testid="login-events-search"
            />
          </div>
        )}

        <div className="flex items-center gap-2 ml-auto">
          <Badge variant="outline" className="bg-slate-50 border-slate-200 text-slate-700 text-xs" data-testid="login-events-stat-total">
            {total.toLocaleString()} event{total === 1 ? "" : "s"}
          </Badge>
          {returned < total && (
            <Badge variant="outline" className="bg-amber-50 border-amber-200 text-amber-800 text-xs">
              Showing latest {returned}
            </Badge>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="login-events-refresh">
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={download} disabled={downloading || events.length === 0} data-testid="login-events-download">
          <Download className="h-3 w-3 mr-1" />{downloading ? "Preparing…" : "Download Excel"}
        </Button>
      </div>

      <div className="flex-1 overflow-auto border rounded-md">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 sticky top-0">
              <TableHead>When</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead>Member #</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>IP address</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Remember me</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="py-8 text-center text-slate-500">Loading…</TableCell></TableRow>
            ) : events.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="py-8 text-center text-slate-500">No login events in this window.</TableCell></TableRow>
            ) : (
              events.map((e) => (
                <TableRow key={e.id} data-testid={`event-row-${e.id}`} className={e.success ? "" : "bg-rose-50/40"}>
                  <TableCell className="text-xs whitespace-nowrap">{formatDate(e.logged_in_at)}</TableCell>
                  <TableCell>
                    {e.success ? (
                      <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200" data-testid={`outcome-${e.id}`}>
                        <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> success
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] bg-rose-100 text-rose-800 border-rose-200" data-testid={`outcome-${e.id}`} title={e.failure_reason || ""}>
                        <XCircle className="h-2.5 w-2.5 mr-0.5" /> {e.failure_reason ? e.failure_reason.replace("_", " ") : "failure"}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{e.member?.member_number || "—"}</TableCell>
                  <TableCell className="text-xs">
                    {e.member ? `${e.member.last_name} ${e.member.first_name}` : <span className="italic text-slate-400">Unknown</span>}
                  </TableCell>
                  <TableCell className="text-xs">{e.email || "—"}</TableCell>
                  <TableCell className="text-xs font-mono">{e.ip || "—"}</TableCell>
                  <TableCell className="text-xs" title={e.user_agent || ""}>{shortUA(e.user_agent)}</TableCell>
                  <TableCell>
                    {e.remember_me ? <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-800 border-blue-200">Yes</Badge> : <span className="text-slate-300 text-xs">—</span>}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ── Main dialog ─────────────────────────────────────────────────────────
const LoginReportDialog = ({ open, onOpenChange }) => {
  const [tab, setTab] = useState("summary");
  const [filterUser, setFilterUser] = useState(null);

  const openHistoryForUser = (u) => {
    setFilterUser(u);
    setTab("audit");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl w-[95vw] max-h-[90vh] flex flex-col"
        data-testid="login-report-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LogIn className="h-5 w-5 text-emerald-600" />
            Login activity
          </DialogTitle>
          <DialogDescription>
            The <strong>Summary</strong> tab shows one row per user with their most recent login. The
            <strong> Audit trail</strong> tab shows every individual sign-in with the source IP and browser.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid grid-cols-2 w-72 mb-2">
            <TabsTrigger value="summary" data-testid="tab-summary">
              <UsersIcon className="h-3.5 w-3.5 mr-1.5" /> Summary
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">
              <History className="h-3.5 w-3.5 mr-1.5" /> Audit trail
            </TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="flex-1 overflow-hidden mt-0">
            <SummaryTab onOpenHistoryForUser={openHistoryForUser} />
          </TabsContent>

          <TabsContent value="audit" className="flex-1 overflow-hidden mt-0">
            <AuditTab filterUser={filterUser} onClearFilter={() => setFilterUser(null)} />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="login-report-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LoginReportDialog;
